import { useEffect, useRef, useState } from 'react'
import { X, Loader2, Trash2, AlertTriangle, ImagePlus, Package, UploadCloud } from 'lucide-react'
import { apiGet, patchJson, postJson } from '../../lib/api.js'
import { uploadFile } from '../../lib/uploadDirect.js'
import { imageUrl } from '../../lib/stock.js'
import { itemName, fetchChoices } from '../../lib/inventory.js'
import Combobox from '../Combobox.jsx'

// Add or edit ONE inventory item — side popup, same shell as StockRecordDrawer.
//
// `item = null` → CREATE mode. Same form; the delete section is hidden (there is
// nothing to delete yet) and the picture is STAGED rather than uploaded — the
// upload endpoint addresses a row by id and there is no id until the row exists,
// so the file waits in memory and is sent immediately after the insert returns
// one. The user still just drops the file in, which is the point.
//
// Editable: ชื่อ · หน่วย · หมวดหมู่ · จุดสั่งซื้อ (min) · หมายเหตุ · รูป.
// NOT editable: the id, and anything derived (levels, movement counts) — those
// are facts about the ledger, not fields.
//
// Deleting is gated on the item having NO stock rows. The button is hidden
// entirely when `movementCount > 0` and the reason is spelled out, because a
// disabled button with no explanation just reads as a bug. The server refuses it
// again regardless (functions/api/inventory.js) — this copy only saves the user
// a round trip.
//
// The picture is written by its OWN endpoint, immediately, not as part of save:
// the browser PUTs the bytes straight to Wasabi with a presigned URL, then posts
// the resulting key to /api/inventory/image?attach=1, which presigns a read and
// hands it to Xano so Xano can store its own copy. Folding that into the form's
// save would mean one button with two failure modes and no way to tell which half
// landed. (The bytes used to travel through the Pages Worker — that is the Error
// 1102 path; functions/api/inventory/image.js explains what changed and why the
// Xano hop cannot move to the client.)

const UNIT_SUGGESTIONS = ['ชิ้น', 'อัน', 'กล่อง', 'ห่อ', 'แพ็ค', 'ขวด', 'หลอด', 'ม้วน', 'ชุด', 'คู่', 'ใบ', 'เส้น']

export default function InventoryEditDrawer({
  item, movementCount, onClose, onSaved, onDeleted,
  // Seed lists from a caller that already has them (the list page). Only a
  // head start — the drawer fetches the authoritative aggregates itself, so it
  // works identically when opened from the detail page, which has no master.
  categoryOptions = [], subCategoryOptions = [], unitOptions = [],
}) {
  const creating = !item?.id
  const fileRef = useRef(null)

  const [name, setName] = useState(item?.Name || '')
  const [unit, setUnit] = useState(item?.unit || '')
  const [mainCategory, setMainCategory] = useState(item?.mainCategory || '')
  const [subCategory, setSubCategory] = useState(item?.subCategory || '')
  const [min, setMin] = useState(item?.min ?? '')
  const [note, setNote] = useState(item?.note || '')
  const [active, setActive] = useState(item?.active !== false)

  const [img, setImg] = useState(imageUrl(item))
  const [uploading, setUploading] = useState(false)
  // CREATE mode only: the chosen file waits here until the row exists.
  const [pendingFile, setPendingFile] = useState(null)
  const [pendingUrl, setPendingUrl] = useState(null)
  const [dragging, setDragging] = useState(false)

  // Object URLs are a manual allocation — revoke the previous one whenever it is
  // replaced, and the last one on unmount, or every re-pick leaks a blob.
  useEffect(() => () => { if (pendingUrl) URL.revokeObjectURL(pendingUrl) }, [pendingUrl])

  /* ── choice lists ───────────────────────────────────────────────────────── */
  // From GET /inventory's mainCAT / subCAT / unit aggregate modes — the whole
  // catalogue, not just what one page of items happens to contain.
  //
  // A value newly typed into one combobox is merged into its own list so it
  // survives until the next fetch: without that, choosing "เพิ่ม X" and then
  // reopening the same dropdown would show X missing, as if it had not stuck.
  const [choices, setChoices] = useState({
    mainCategory: categoryOptions,
    subCategory: subCategoryOptions,
    // UNIT_SUGGESTIONS is the fallback only — real units in use always win.
    unit: unitOptions.length ? unitOptions : UNIT_SUGGESTIONS,
  })

  useEffect(() => {
    let alive = true
    Promise.all([
      fetchChoices(apiGet, 'mainCategory').catch(() => null),
      fetchChoices(apiGet, 'subCategory').catch(() => null),
      fetchChoices(apiGet, 'unit').catch(() => null),
    ]).then(([main, sub, u]) => {
      if (!alive) return
      // A failed list falls back to the seed rather than emptying the dropdown —
      // an empty picker reads as "there are no categories", which is a lie.
      setChoices((prev) => ({
        mainCategory: main || prev.mainCategory,
        subCategory: sub || prev.subCategory,
        unit: (u && u.length) ? u : prev.unit,
      }))
    })
    return () => { alive = false }
  }, [])

  // Merge the record's OWN value in, so an item already using a category that
  // the aggregate does not return still shows it as the current selection.
  const optionsFor = (field, current) => {
    const list = choices[field] || []
    const v = String(current || '').trim()
    return v && !list.some((o) => o.toLowerCase() === v.toLowerCase()) ? [v, ...list] : list
  }

  // NOTE: a newly typed value is NOT pushed into `choices`. With `freeText` the
  // combobox commits on every keystroke, so appending here would file "D",
  // "De", "Den"… as categories. `optionsFor` already surfaces the current value
  // at the top of its own list, which is all the feedback the field needs.
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [confirmDel, setConfirmDel] = useState(false)
  const [error, setError] = useState(null)

  const busy = saving || deleting || uploading
  // `movementCount == null` means the count is unknown (the caller's check
  // failed). Unknown is NOT zero: offering a delete we cannot justify would
  // just bounce off the proxy's own guard, so the section explains instead.
  const known = movementCount != null && Number.isFinite(Number(movementCount))
  const linked = known ? Number(movementCount) : 0
  const deletable = known && linked === 0

  const dirty = creating ? !!name.trim() :
    (item?.Name || '') !== name ||
    (item?.unit || '') !== unit ||
    (item?.mainCategory || '') !== mainCategory ||
    (item?.subCategory || '') !== subCategory ||
    String(item?.min ?? '') !== String(min) ||
    (item?.note || '') !== note ||
    (item?.active !== false) !== active

  const nameValid = !!name.trim()

  async function save() {
    setSaving(true)
    setError(null)
    const fields = {
      Name: name.trim(),
      unit: unit.trim(),
      mainCategory: mainCategory.trim(),
      subCategory: subCategory.trim(),
      // '' → 0: "no reorder point" and "reorder at 0" are the same thing to the
      // ledger, and lib/inventory.js#belowMin treats 0 as untracked.
      min: Number(min) || 0,
      note,
      active,
    }
    try {
      if (creating) {
        // POST /inventory is an upsert keyed on inventory_id, same shape as
        // POST /stock — 0 means insert, and it is sent explicitly rather than
        // omitted so add_or_edit can resolve add-vs-edit (a null cannot).
        const created = await postJson('/api/inventory', { inventory_id: 0, ...fields })
        const newId = Number(created?.id ?? created?.inventory?.id) || 0
        // The item now exists, so a staged picture finally has something to
        // attach to. A failure here does NOT roll back the insert — the row is
        // real and the user can add the picture from its own page; uploadImage
        // has already surfaced why. Reported, never silent.
        if (pendingFile && newId) await uploadImage(newId, pendingFile, { notify: false })
        onSaved?.(created)
      } else {
        await patchJson('/api/inventory', { inventory_id: item.id, ...fields })
        onSaved?.()
      }
      onClose?.()
    } catch (e) {
      setError(readableError(e, 'บันทึกไม่สำเร็จ'))
      setSaving(false)
    }
  }

  /**
   * A file arrived — from the picker, a drop, or a paste. Validated HERE rather
   * than at each entry point: a drop can carry anything (a PDF, a folder, a
   * dragged link), and the picker's `accept` filter is a hint the OS is free to
   * ignore. The proxy checks again; this only saves a pointless round trip.
   */
  function acceptFile(file) {
    if (!file) return
    if (!/^image\//i.test(file.type || '')) {
      setError('ไฟล์นี้ไม่ใช่รูปภาพ — รองรับ JPG · PNG · WebP · GIF · HEIC')
      return
    }
    if (file.size > 8 * 1024 * 1024) {
      setError('ไฟล์ใหญ่เกิน 8MB')
      return
    }
    setError(null)
    if (creating) {
      // No id to attach to yet — hold it and preview locally. save() sends it.
      if (pendingUrl) URL.revokeObjectURL(pendingUrl)
      setPendingFile(file)
      setPendingUrl(URL.createObjectURL(file))
      return
    }
    uploadImage(item.id, file)
  }

  // `notify` off during create: save() fires its own onSaved with the new record
  // right after, and two calls would make the list refresh then navigate.
  async function uploadImage(inventoryId, file, { notify = true } = {}) {
    setUploading(true)
    try {
      // 🔑 The bytes go browser → Wasabi and never through the Pages Worker. This
      // used to be a multipart POST that buffered the whole image in the 128 MB
      // isolate every /api route shares, which is how an upload served Cloudflare
      // Error 1102 to someone else's page load. uploadFile keeps that multipart
      // proxy as its own automatic fallback for a direct PUT the browser cannot
      // make — see src/lib/uploadDirect.js.
      //
      // `{ attach: true }` is NOT optional here, and it is not merely a shape.
      // This route keeps its Xano write on the server (it is signed with our
      // Wasabi key and stamps employee_id from the session), so its presign
      // answers `attach_required: true` and NO `result` — the contract written
      // up at the top of functions/api/me/files.js. The flag is what routes the
      // second hop through `viaAttach`, and viaAttach is the ONLY caller that
      // never falls back: once the bytes are in the bucket, a Xano failure is
      // the answer, not a reason to re-upload through the isolate. This used to
      // be a hand-rolled `postJson(…?attach=1)` right here, which worked but
      // took the fallback semantics with it — a Xano hiccup after a perfectly
      // good PUT would mark the direct path broken and send every later upload
      // in the tab back through the Worker, i.e. back onto the 1102 path this
      // whole change exists to empty. Do not unroll it again.
      //
      // Both roads return the same thing — the Xano record — because ?attach=1
      // and the multipart fallback share `handToXano`. So there is nothing to
      // branch on below.
      const out = (await uploadFile(
        '/api/inventory/image',
        { inventory_id: inventoryId },
        file,
        { attach: true },
      )) || {}
      // Xano returns the updated record; fall back to a local preview so the
      // user sees their own file even if the response shape surprises us.
      setImg(imageUrl(out) || imageUrl(out?.inventory) || URL.createObjectURL(file))
      if (notify) onSaved?.()
      return true
    } catch (e) {
      setError(readableError(e, 'อัปโหลดรูปไม่สำเร็จ'))
      return false
    } finally {
      setUploading(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  function onDrop(e) {
    e.preventDefault()
    setDragging(false)
    if (busy) return
    // `items` distinguishes a real file from a dragged link/selection, which
    // `files` reports as an empty list on some browsers rather than an error.
    const dt = e.dataTransfer
    const file = dt?.files?.[0]
    if (!file) {
      setError('ลากไฟล์รูปจากเครื่องมาวางเท่านั้น (ลากรูปจากหน้าเว็บอื่นไม่ได้)')
      return
    }
    acceptFile(file)
  }

  async function removeImage() {
    setUploading(true)
    setError(null)
    try {
      const res = await fetch('/api/inventory/image', {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inventory_id: item.id }),
      })
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`)
      setImg(null)
      onSaved?.()
    } catch (e) {
      setError(readableError(e, 'ลบรูปไม่สำเร็จ'))
    } finally {
      setUploading(false)
    }
  }

  async function remove() {
    setDeleting(true)
    setError(null)
    try {
      const res = await fetch('/api/inventory', {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ inventory_id: item.id }),
      })
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`)
      onDeleted?.()
    } catch (e) {
      setError(readableError(e, 'ลบไม่สำเร็จ'))
      setDeleting(false)
      setConfirmDel(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={() => !busy && onClose?.()} />
      {/* Swallow drops that MISS the zone. Without this the browser navigates
          the whole tab to the dropped file, throwing away a half-filled form —
          the worst possible outcome of a near-miss. */}
      <div className="relative w-full max-w-[480px] bg-slate-50 shadow-2xl flex flex-col animate-ss-slide-in"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => e.preventDefault()}>

        <div className="flex items-start justify-between gap-3 border-b border-slate-200 bg-white px-5 py-4">
          <div className="min-w-0">
            <div className="text-[15.5px] font-semibold text-slate-900 truncate">
              {creating ? 'เพิ่มของใหม่' : 'แก้ไขข้อมูลของ'}
            </div>
            <div className="text-[12.5px] text-slate-400 truncate">
              {creating ? 'เพิ่มรายการเข้าคลังของ' : <>{itemName(item)} · #{item?.id}</>}
            </div>
          </div>
          <button onClick={() => !busy && onClose?.()} className="text-slate-400 hover:text-slate-600 shrink-0">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-5">
          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-800">{error}</div>
          )}

          {/* Picture — drag & drop OR click. The whole zone is the drop target,
              not just the thumbnail: a 96px square is a hard thing to aim a
              dragged file at, and a drop that lands 10px outside navigates the
              browser away to the file instead, losing the half-filled form. */}
          <section>
            <div className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-2">รูป</div>
            <div
              onDragEnter={(e) => { e.preventDefault(); if (!busy) setDragging(true) }}
              onDragOver={(e) => { e.preventDefault(); if (!busy) setDragging(true) }}
              onDragLeave={(e) => {
                // Only clear when the pointer leaves the ZONE — dragging across a
                // child element fires dragleave on the parent and would otherwise
                // flicker the highlight off mid-drag.
                if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false)
              }}
              onDrop={onDrop}
              onClick={() => !busy && fileRef.current?.click()}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !busy) fileRef.current?.click() }}
              className={`rounded-xl border-2 border-dashed p-4 transition cursor-pointer ${
                dragging
                  ? 'border-brand-400 bg-brand-50'
                  : 'border-slate-200 bg-white hover:border-brand-300 hover:bg-brand-50/30'
              } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
            >
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center shrink-0">
                  {(pendingUrl || img)
                    ? <img src={pendingUrl || img} alt="" className="w-full h-full object-cover" />
                    : <Package size={26} className="text-slate-300" />}
                </div>
                <div className="min-w-0">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden"
                    onChange={(e) => { acceptFile(e.target.files?.[0]); e.target.value = '' }} />
                  <div className="flex items-center gap-1.5 text-[13.5px] font-medium text-slate-700">
                    {uploading
                      ? <><Loader2 size={15} className="animate-spin text-brand-600" />กำลังอัปโหลด…</>
                      : dragging
                        ? <><UploadCloud size={15} className="text-brand-600" />วางไฟล์ที่นี่</>
                        : <><ImagePlus size={15} className="text-slate-400" />{(pendingUrl || img) ? 'เปลี่ยนรูป' : 'อัปโหลดรูป'}</>}
                  </div>
                  <div className="mt-1 text-[12px] text-slate-500">ลากไฟล์มาวาง หรือคลิกเพื่อเลือก</div>
                  <div className="mt-0.5 text-[11.5px] text-slate-400">JPG · PNG · WebP — ไม่เกิน 8MB</div>
                  {/* Staged, not stored — say so plainly, so nobody closes the
                      drawer believing the picture is already saved. */}
                  {pendingFile && (
                    <div className="mt-1.5 text-[11.5px] text-brand-700">
                      เลือกไว้แล้ว: {pendingFile.name} — จะอัปโหลดเมื่อกด “เพิ่มของ”
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Outside the drop zone: a click on it must not re-open the picker. */}
            {!creating && img && (
              <button onClick={removeImage} disabled={busy}
                className="mt-2 text-[13px] text-slate-500 hover:text-rose-600 disabled:opacity-50">
                ลบรูป
              </button>
            )}
            {creating && pendingFile && (
              <button
                onClick={() => {
                  if (pendingUrl) URL.revokeObjectURL(pendingUrl)
                  setPendingFile(null); setPendingUrl(null)
                }}
                disabled={busy}
                className="mt-2 text-[13px] text-slate-500 hover:text-rose-600 disabled:opacity-50">
                เอารูปออก
              </button>
            )}
          </section>

          <section className="space-y-3.5">
            <Field label="ชื่อของ">
              <input value={name} onChange={(e) => setName(e.target.value)} disabled={busy}
                className={`w-full rounded-lg border bg-white px-3 py-2 text-[14px] text-slate-800 outline-none focus:ring-2 focus:ring-brand-100 ${
                  nameValid ? 'border-slate-200 focus:border-brand-300' : 'border-rose-300'
                }`} />
              {!nameValid && <p className="mt-1 text-[12px] text-rose-600">ชื่อของห้ามว่าง</p>}
            </Field>

            {/* All three are searchable dropdowns that can also mint a new
                value (`allowCreate`) — categories and units are free-text
                columns on the item, not a lookup table, so "creating" one is
                just being the first item to use that string. The explicit
                ＋ row exists so that is a deliberate act rather than a typo
                nobody notices until the list has both "ชุด" and "ชุด ". */}
            <Field label="หน่วย">
              <Combobox
                value={unit}
                onChange={setUnit}
                options={optionsFor('unit', unit)}
                placeholder="เลือก / ค้นหาหน่วย เช่น ชิ้น · กล่อง · ขวด"
                disabled={busy}
                freeText
                allowCreate
                createLabel={(q) => `เพิ่มหน่วยใหม่ “${q}”`}
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="หมวดหมู่หลัก">
                <Combobox
                  value={mainCategory}
                  onChange={setMainCategory}
                  options={optionsFor('mainCategory', mainCategory)}
                  placeholder="เลือก / ค้นหาหมวดหมู่"
                  disabled={busy}
                  freeText
                  allowCreate
                  createLabel={(q) => `เพิ่มหมวดหมู่ใหม่ “${q}”`}
                />
              </Field>
              <Field label="หมวดหมู่ย่อย">
                <Combobox
                  value={subCategory}
                  onChange={setSubCategory}
                  options={optionsFor('subCategory', subCategory)}
                  placeholder="เลือก / ค้นหาหมวดย่อย"
                  disabled={busy}
                  freeText
                  allowCreate
                  createLabel={(q) => `เพิ่มหมวดย่อยใหม่ “${q}”`}
                />
              </Field>
            </div>

            <Field label="จุดสั่งซื้อขั้นต่ำ (ทั้งคลินิก)">
              <input type="number" min="0" value={min} onChange={(e) => setMin(e.target.value)} disabled={busy}
                placeholder="0 = ไม่ติดตาม"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 tabular-nums placeholder:text-slate-400 outline-none focus:border-brand-300 focus:ring-2 focus:ring-brand-100" />
            </Field>

            <Field label="หมายเหตุ">
              <textarea value={note} onChange={(e) => setNote(e.target.value)} disabled={busy} rows={3}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 outline-none focus:border-brand-300 focus:ring-2 focus:ring-brand-100 resize-none" />
            </Field>

            <label className="flex items-center gap-2.5 text-[13.5px] text-slate-700">
              <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} disabled={busy}
                className="rounded border-slate-300 text-brand-600 focus:ring-brand-200" />
              ใช้งานอยู่
              <span className="text-[12px] text-slate-400">— ปิดเพื่อซ่อนจากรายการเบิก โดยไม่ลบประวัติ</span>
            </label>
          </section>

          {/* Delete — last, and never a bare button. Nothing to delete yet
              while creating. */}
          {!creating && (
          <section className="border-t border-slate-200 pt-4">
            {deletable ? (
              confirmDel ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                  <div className="flex items-start gap-2.5 text-[13px] text-rose-800">
                    <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                    <div>ลบ “{itemName(item)}” ออกจากคลังของถาวร? ย้อนกลับไม่ได้</div>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button onClick={remove} disabled={busy}
                      className="rounded-lg bg-rose-600 px-4 py-2 text-[13.5px] font-semibold text-white hover:bg-rose-700 disabled:opacity-60 flex items-center gap-2">
                      {deleting && <Loader2 size={14} className="animate-spin" />}ลบถาวร
                    </button>
                    <button onClick={() => setConfirmDel(false)} disabled={busy}
                      className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-[13.5px] text-slate-600">
                      ยกเลิก
                    </button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setConfirmDel(true)} disabled={busy}
                  className="text-[13.5px] text-slate-500 hover:text-rose-600 flex items-center gap-1.5">
                  <Trash2 size={14} />ลบของชิ้นนี้
                </button>
              )
            ) : (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-[12.5px] text-slate-500">
                {known
                  ? <>ลบไม่ได้ — มีรายการเคลื่อนไหว <b className="text-slate-700 tabular-nums">{linked.toLocaleString('en-US')}</b> รายการที่อ้างถึงของชิ้นนี้</>
                  : <>ตรวจสอบรายการเคลื่อนไหวไม่สำเร็จ จึงยังลบไม่ได้</>}
                <div className="mt-1 text-slate-400">ถ้าเลิกใช้แล้ว ให้ปิด “ใช้งานอยู่” แทน — ประวัติจะยังอ่านได้</div>
              </div>
            )}
          </section>
          )}
        </div>

        <div className="border-t border-slate-200 bg-white px-5 py-3.5 flex items-center justify-end gap-2">
          <button onClick={() => !busy && onClose?.()} disabled={busy}
            className="rounded-xl border border-slate-200 px-4 py-2 text-[14px] text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            ปิด
          </button>
          <button onClick={save} disabled={busy || !dirty || !nameValid}
            className="rounded-xl bg-brand-600 px-5 py-2 text-[14px] font-semibold text-white hover:bg-brand-700 disabled:opacity-40 flex items-center gap-2">
            {saving && <Loader2 size={15} className="animate-spin" />}{creating ? 'เพิ่มของ' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[12px] font-medium uppercase tracking-wide text-slate-400 mb-1.5">{label}</div>
      {children}
    </label>
  )
}

// The proxies answer with a JSON body carrying a Thai `detail`; surface that
// instead of "HTTP 409: {...}".
function readableError(e, fallback) {
  const raw = e?.message || ''
  const m = raw.match(/\{[\s\S]*\}$/)
  if (m) {
    try {
      const j = JSON.parse(m[0])
      if (j?.detail) return j.detail
    } catch { /* fall through */ }
  }
  return raw || fallback
}
